/* eslint-disable react/prop-types */
import React from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import useTabData from './useTabData';
import { TabLoading, TabError, TabEmpty } from './TabStates';
import { q, Kpi } from './kit';
import PauseRecordsTab from './PauseRecordsTab';
import { showTimeInSec } from '../../../utils/utils';

// Aggregate per-day breathing vs meditation counts from the two record lists.
const perDay = (medRows, brRows) => {
  const map = new Map();
  const bump = (rows, key) => rows.forEach((r) => {
    const day = (r.timestamp_utc || '').slice(0, 10);
    if (!day) return;
    const e = map.get(day) || { date: day, breathing: 0, meditation: 0 };
    e[key] += 1;
    map.set(day, e);
  });
  bump(brRows, 'breathing');
  bump(medRows, 'meditation');
  return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
};

// avg_duration is milliseconds — same "2 minutes 5 seconds" format as /breath-list.
const duration = (ms) => (ms == null || Number.isNaN(Number(ms)) ? '—' : showTimeInSec(Number(ms)) || '0 seconds');

const PausesTab = ({ userId, from, to, authGet }) => {
  const { status, data, reload } = useTabData(async () => {
    const [medSum, brSum, medList, brList] = await Promise.all([
      authGet(`/admin/api/pauses-summary?${q(userId, from, to)}`),
      authGet(`/admin/api/breath-summary?${q(userId, from, to)}`),
      authGet(`/admin/api/pauses?${q(userId, from, to, { page: 1, limit: 200 })}`),
      authGet(`/admin/api/breath?${q(userId, from, to, { page: 1, limit: 200 })}`),
    ]);
    return {
      med: medSum.data.data || {},
      br: brSum.data.data || {},
      medRows: medList.data.data || [],
      brRows: brList.data.data || [],
    };
  }, [userId, from, to]);

  if (status === 'loading') return <TabLoading />;
  if (status === 'error') return <TabError onRetry={reload} />;

  const { med, br, medRows, brRows } = data;
  const meditation = Number(med.total_sessions) || 0;
  const breathing = Number(br.total_sessions) || 0;
  const total = meditation + breathing;
  if (total === 0) {
    return <TabEmpty title="No pauses" message="This user took no breathing or meditation pauses in this window." icon="❋" />;
  }
  const pct = (n) => (total ? Math.round((n / total) * 100) : 0);
  const days = perDay(medRows, brRows);

  return (
    <div data-testid="ud-pauses">
      <div className="ud-kpis">
        <Kpi label="Total pauses" value={total} />
        <Kpi label="Breathing" value={breathing} sub={`${pct(breathing)}% of sessions`} />
        <Kpi label="Meditation" value={meditation} sub={`${pct(meditation)}% of sessions`} />
        <Kpi label="Avg breath duration" value={duration(br.avg_duration)} />
      </div>

      <div className="au-panel">
        <div className="au-panel__head"><h4 className="au-panel__title">Pauses per day</h4></div>
        <div style={{ padding: '12px 8px' }}>
          {days.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={days} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f1" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="breathing" stackId="p" fill="#2e9b87" name="Breathing" isAnimationActive={false} />
                <Bar dataKey="meditation" stackId="p" fill="#163a5f" name="Meditation" isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="au-chart-empty">No per-day pause detail to plot.</div>
          )}
        </div>
      </div>

      <h4 className="ud-section-title">Pause records</h4>
      <PauseRecordsTab userId={userId} from={from} to={to} authGet={authGet} />
    </div>
  );
};

export default PausesTab;
